import { Capacitor } from './capacitorShim';

/**
 * The subset of a fix every call site in the app actually consumes.
 * Deliberately narrower than the browser's `GeolocationCoordinates` so the
 * native and web paths can return the same shape.
 */
export interface GeoPositionModel {
	latitude: number;
	longitude: number;
	accuracy?: number;
}

/**
 * Why a position read failed, normalised across the native plugin and the
 * browser API so callers can phrase an actionable message without knowing
 * which provider answered.
 */
export type GeoErrorReason =
	| 'unsupported'
	| 'permission-denied'
	| 'unavailable'
	| 'timeout';

export class GeoPositionError extends Error {
	public readonly reason: GeoErrorReason;

	public constructor(reason: GeoErrorReason, message: string) {
		super(message);
		this.name = 'GeoPositionError';
		this.reason = reason;
	}
}

export interface GeoPositionOptions {
	enableHighAccuracy?: boolean;
	/** Milliseconds before the attempt is abandoned. */
	timeout?: number;
	/** Maximum age of an acceptable cached fix, in milliseconds. */
	maximumAge?: number;
}

const DEFAULT_OPTIONS: Required<GeoPositionOptions> = {
	enableHighAccuracy: true,
	timeout: 10000,
	maximumAge: 0,
};

interface CapacitorPositionLike {
	coords: { latitude: number; longitude: number; accuracy?: number };
}

interface GeolocationPluginLike {
	getCurrentPosition: (
		options?: GeoPositionOptions,
	) => Promise<CapacitorPositionLike>;
	checkPermissions: () => Promise<{ location: string }>;
	requestPermissions: () => Promise<{ location: string }>;
}

let hasLoaded = false;
let plugin: GeolocationPluginLike | null = null;

/**
 * Lazily resolve @capacitor/geolocation, mirroring `capacitorShim`: the
 * require is deferred to first call and skipped entirely off-browser, so the
 * Node-based APIs never resolve a module that `pnpm deploy --prod` prunes.
 */
function loadPlugin(): GeolocationPluginLike | null {
	if (hasLoaded) {
		return plugin;
	}

	hasLoaded = true;

	if (typeof window === 'undefined') {
		return null;
	}

	try {
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		const mod: unknown = require('@capacitor/geolocation');
		const candidate = (mod as { Geolocation?: GeolocationPluginLike })
			.Geolocation;
		plugin = candidate ?? null;
	} catch {
		// Not present in this runtime — callers fall back to the web API.
	}

	return plugin;
}

/**
 * Read the device's current position, using the native plugin when running
 * inside the Capacitor shell and the browser API everywhere else.
 *
 * Why this exists (PD-14866): calling `navigator.geolocation` from inside the
 * app means the request travels through WKWebView, which raises its own
 * permission prompt *in addition to* the OS one — so a driver was asked twice,
 * at every stop. Going through the plugin talks to CoreLocation directly and
 * produces a single prompt.
 *
 * Rejects if no provider is available or the fix fails; see
 * `tryGetCurrentPosition` for the best-effort variant most call sites want.
 */
export async function getCurrentPosition(
	options?: GeoPositionOptions,
): Promise<GeoPositionModel> {
	const resolved = { ...DEFAULT_OPTIONS, ...options };
	const nativePlugin = Capacitor.isNativePlatform() ? loadPlugin() : null;

	if (nativePlugin != null) {
		// Android requires an explicit grant before a fix; iOS prompts as part
		// of the read. Checking first keeps the Android path from failing with
		// a bare permission error on its first use.
		const status = await nativePlugin.checkPermissions();

		if (status.location !== 'granted') {
			const requested = await nativePlugin.requestPermissions();

			if (requested.location === 'denied') {
				throw new GeoPositionError(
					'permission-denied',
					'Location permission was denied',
				);
			}
		}

		try {
			const position = await nativePlugin.getCurrentPosition(resolved);

			return {
				latitude: position.coords.latitude,
				longitude: position.coords.longitude,
				accuracy: position.coords.accuracy,
			};
		} catch (error) {
			// The plugin surfaces failures as plain messages rather than codes,
			// so the reason is inferred from the text — good enough to phrase an
			// actionable message, and it degrades to 'unavailable'.
			const message = error instanceof Error ? error.message : String(error);
			const lowered = message.toLowerCase();

			throw new GeoPositionError(
				lowered.includes('denied') || lowered.includes('permission')
					? 'permission-denied'
					: lowered.includes('timed out') || lowered.includes('timeout')
						? 'timeout'
						: 'unavailable',
				message,
			);
		}
	}

	if (
		typeof navigator === 'undefined' ||
		// The DOM lib types this as always-present, but it is genuinely absent
		// in some embedded web views and on insecure origins.
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		navigator.geolocation == null
	) {
		throw new GeoPositionError(
			'unsupported',
			'Geolocation is not available in this runtime',
		);
	}

	const position = await new Promise<GeolocationPosition>((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			resolve,
			(error) => {
				reject(
					new GeoPositionError(
						error.code === error.PERMISSION_DENIED
							? 'permission-denied'
							: error.code === error.TIMEOUT
								? 'timeout'
								: 'unavailable',
						error.message,
					),
				);
			},
			resolved,
		);
	});

	return {
		latitude: position.coords.latitude,
		longitude: position.coords.longitude,
		accuracy: position.coords.accuracy,
	};
}

/**
 * Best-effort position read: resolves to an empty object rather than throwing
 * when the fix is unavailable, refused, or times out.
 *
 * Most call sites are stamping a coordinate onto an action that must succeed
 * regardless (completing a stop, checking in), so a missing fix is expected
 * and must never block the user.
 */
export async function tryGetCurrentPosition(
	options?: GeoPositionOptions,
): Promise<Partial<GeoPositionModel>> {
	try {
		return await getCurrentPosition(options);
	} catch (error) {
		console.debug('GPS not available:', error);
		return {};
	}
}
